import axios, {AxiosResponse} from "axios";
import {Invoice, InvoicePenaltyHistoryModel, PenaltyRepaymentModel, SendInvoicesToClientRequest} from "../types/types";
import {UseToast} from "../hooks/useToast";
import {ExportExcelParams} from "react-ive-tables/dist/ReactiveTable";
import moment from "moment";
import {InvoiceIconsOverlay} from "../../components/invoices/invoiceIcons/InvoiceIconsOverlay";

export class InvoiceService {
    private readonly url: string;
    private readonly toastHelpers: Pick<UseToast, 'showDeletedToast' | 'showUpdatedToast' | 'showCreatedToast' | 'showToast'>
    private readonly f: any;

    constructor(f: any, toastHelpers: Pick<UseToast, 'showDeletedToast' | 'showUpdatedToast' | 'showCreatedToast' | 'showToast'>) {
        this.toastHelpers = toastHelpers;
        this.f = f;
        this.url = '/invoices';
        this.uploadInvoiceFile = this.uploadInvoiceFile.bind(this);
        this.downloadInvoiceFile = this.downloadInvoiceFile.bind(this);
        this.deleteInvoiceFile = this.deleteInvoiceFile.bind(this);
    }

    async update(Invoice: Invoice) {
        return new Promise<Invoice>((resolve, reject) => {
            axios.put<Invoice>(this.url, Invoice).then(response => {
                resolve(response.data)
                this.toastHelpers.showUpdatedToast(this.f, this.f({id: 'theInvoice'}), 'а', true);
            }).catch(error => {
                if (error.response.status === 409) {
                    const message = error.response.data.message;
                    if (message) {
                        this.toastHelpers.showToast('warn', this.f({id: 'operationUnsuccessful'}), this.f({id: message}))
                    }
                } else {
                    this.toastHelpers.showUpdatedToast(this.f, this.f({id: 'theInvoice'}), 'а', false);
                }
                reject(error);
            })
        })
    }

    async delete(invoiceId: number) {
        return new Promise<boolean>((resolve, reject) => {
            axios.delete<boolean>(this.url + '/' + invoiceId).then(response => {
                resolve(response.data)
                this.toastHelpers.showDeletedToast(this.f, this.f({id: 'theInvoice'}), 'а', true);
            }).catch(error => {
                reject(error);
                this.toastHelpers.showDeletedToast(this.f, this.f({id: 'theInvoice'}), 'а', false);
            })
        })
    }

    async uploadNsysInvoice(file: File) {
        return new Promise<boolean>((resolve, reject) => {
            let formData = new FormData();
            formData.append("file", file);
            const headers = {'Content-Type': 'multipart/form-data'};
            axios.post(this.url + "/importNsys", formData, {headers}).then(response => {
                resolve(response.data)
            }).catch(err => reject(err))
        })
    }

    async uploadNexusInvoice(file: File) {
        return new Promise<boolean>((resolve, reject) => {
            let formData = new FormData();
            formData.append("file", file);
            const headers = {'Content-Type': 'multipart/form-data'};
            axios.post(this.url + "/importNexus", formData, {headers}).then(response => {
                resolve(response.data)
            }).catch(err => reject(err))
        })
    }

    async uploadRedLoriInvoice(file: File) {
        return new Promise<boolean>((resolve, reject) => {
            let formData = new FormData();
            formData.append("file", file);
            const headers = {'Content-Type': 'multipart/form-data'};
            axios.post(this.url + "/redlori", formData, {headers}).then(response => {
                resolve(response.data)
            }).catch(err => reject(err))
        })
    }

    async uploadRedLoriHtmlInvoice(file: File) {
        return new Promise<boolean>((resolve, reject) => {
            let formData = new FormData();
            formData.append("file", file);
            const headers = {'Content-Type': 'multipart/form-data'};
            axios.post(this.url + "/redlori-html", formData, {headers}).then(response => {
                resolve(response.data)
            }).catch(err => reject(err))
        })
    }

    async uploadMicroInvest(file: File) {
        return new Promise<boolean>((resolve, reject) => {
            let formData = new FormData();
            formData.append("file", file);
            const headers = {'Content-Type': 'multipart/form-data'};
            axios.post(this.url + "/importMicroInvest", formData, {headers}).then(response => {
                resolve(response.data)
            }).catch(err => reject(err))
        })
    }

    async sendInvoicesToClient(data: SendInvoicesToClientRequest) {
        return new Promise<boolean>((resolve, reject) => {
            axios.post(this.url + "/sendInvoicesToClient", data).then(response => {
                resolve(response.data)
            }).catch(err => reject(err))
        })
    }

    async exportExcel(params: Omit<ExportExcelParams, "sort"> & { order?: Record<string, string> }) {
        return new Promise<AxiosResponse<BlobPart>>((resolve, reject) => {
            axios.post<BlobPart>(this.url + "/generateExcel", params, {responseType: 'arraybuffer'}).then(response => {
                resolve(response)
            }).catch(err => reject(err))
        })
    }

    async exportTransactionInvoicesExcel(params: Omit<ExportExcelParams, "sort"> & { order?: Record<string, string> }) {
        return new Promise<AxiosResponse<BlobPart>>((resolve, reject) => {
            axios.post<BlobPart>(this.url + "/generateTransactionsExcel", params, {responseType: 'arraybuffer'}).then(response => {
                resolve(response)
            }).catch(err => reject(err))
        })
    }


    async preparePenaltiesPayment(clientId: number, amount: number, selectedInvoices: string[]) {
        return new Promise<AxiosResponse<BlobPart>>((resolve, reject) => {
            axios.post<BlobPart>(this.url + "/preparePenaltiesPayment", {
                clientId,
                amount,
                selectedInvoices
            }, {responseType: 'arraybuffer'}).then(response => {
                resolve(response);
            })
        })
    }

    async payPenalties(clientId: number, amount: number, selectedInvoices: string[], note?: string) {
        return new Promise<string | null>((resolve, reject) => {
            axios.post<string | null>(this.url + "/payPenalties", {
                clientId,
                amount,
                selectedInvoices,
                note
            }).then(response => {
                if (response.data) {
                    resolve(response.data);
                } else {
                    reject(false);
                    this.toastHelpers.showToast("error", this.f({id: 'operationUnsuccessful'}), '', false);
                }
            }).catch(err => {
                reject(err);
                this.toastHelpers.showToast("error", this.f({id: 'operationUnsuccessful'}), '', false);
            })
        })
    }

    async uploadInvoiceFile(file: any, invoiceId: number) {
        let formData = new FormData();
        formData.append("file", file);
        const headers = {'Content-Type': 'multipart/form-data'};
        return new Promise<any>((resolve, reject) => {
            axios.post(this.url + "/files/" + invoiceId, formData, {headers})
                .then(response => {
                    resolve(response);
                })
        })
    }

    async deleteInvoiceFile(fileId: number) {
        return new Promise<boolean>((resolve, reject) => {
            axios.delete<boolean>(this.url + "/files/" + fileId).then(response => {
                resolve(response.data)
            })
        })
    }

    async downloadInvoiceFile(fileId: number) {
        return new Promise<any>((resolve, reject) => {
            axios.get(this.url + "/files/" + fileId, {responseType: 'arraybuffer'}).then(response => {
                const blob = new Blob([response.data], {
                    type: response.headers['content-type'],
                });
                resolve(blob);
            }).catch(reject);
        })
    }

    async invoicePenaltyHistoryByDate(date: Date, invoiceId: number) {
        date.setHours(23);
        date.setMinutes(59);
        date.setSeconds(59);
        return new Promise<InvoicePenaltyHistoryModel>((resolve, reject) => {
            axios.get<InvoicePenaltyHistoryModel>(`${this.url}/${invoiceId}/penalties/forDate/${moment(date).format()}`)
                .then(resp => {
                    resolve(resp.data)
                })
        })
    }

    async payInvoicePenalty(invoiceId: number, data: PenaltyRepaymentModel) {
        return new Promise<boolean>((resolve, reject) => {
            axios.post<boolean>(`${this.url}/${invoiceId}/penalties/repayment`, data)
                .then(resp => {
                    if (resp.data) {
                        resolve(resp.data);
                        this.toastHelpers.showToast("success", this.f({id: 'operationSuccessful'}), `${this.f({id: 'invoice'})} ${resp.data}`, false);
                    } else {
                        reject(false);
                        this.toastHelpers.showToast("error", this.f({id: 'operationUnsuccessful'}), '', false);
                    }
                }).catch(err => {
                reject(err);
                this.toastHelpers.showToast("error", this.f({id: 'operationUnsuccessful'}), '', false);
            })
        })
    }

    async updateInvoiceIcon(invoiceId: number, invoiceIcon: string): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            axios.put<boolean>(`${this.url}/${invoiceId}/invoiceIcon/${invoiceIcon}`).then(res => {
                const data = res.data;
                resolve(data);
            }).catch(err => {
                reject(err);
            })
        });
    }

    async removeInvoiceIcon(invoiceId: number): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            axios.put<boolean>(`${this.url}/${invoiceId}/removeIcon`).then(res => {
                const data = res.data;
                resolve(data);
            }).catch(err => {
                reject(err);
            })
        });
    }
}
